import React, { useState } from 'react';
import { Lightbulb, Send } from 'lucide-react';
import { useToast } from './TechComponents';
import { captureContext } from '../utils/bugReportCapture';

/**
 * FeatureRequestInlineForm — Formulaire compact « Proposer une idée » intégré au
 * HelpPanel. Jumeau allégé de BugReportInlineForm : pas de capture console/réseau
 * (mode light), deux selects métier (zone + nature), POST kind='feature' vers
 * /api/bug-reports. Toast de succès « Idée #N ».
 *
 * Porte data-bug-capture-skip="true" pour être exclu du screenshot html2canvas.
 */
const ZONES = [
  { value: 'tickets',     label: 'Tickets / Hotline' },
  { value: 'parc',        label: 'Parc / Inventaire' },
  { value: 'boutique',    label: 'Boutique / Catalogue' },
  { value: 'compta',      label: 'Compta / Facturation' },
  { value: 'supervision', label: 'Supervision / Réseau' },
  { value: 'autre',       label: 'Autre' },
];

const NATURES = [
  { value: 'new_feature', label: 'Nouvelle fonctionnalité' },
  { value: 'improvement', label: 'Amélioration existante' },
  { value: 'time_saver',  label: 'Gain de temps / Automatisation' },
  { value: 'ux',          label: 'Confort / UX' },
  { value: 'integration', label: 'Intégration externe' },
];

const MAX_TITLE = 200;
const MAX_DESCRIPTION = 10000;

export default function FeatureRequestInlineForm({ onClose, onSubmitted }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [zone, setZone] = useState('autre');
  const [nature, setNature] = useState('new_feature');
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;

    const t = title.trim();
    const d = description.trim();
    if (!t) {
      toast.warning('Donne un titre court à ton idée avant d\'envoyer.');
      return;
    }
    if (!d) {
      toast.warning('Décris ton idée et le bénéfice attendu avant d\'envoyer.');
      return;
    }

    setSubmitting(true);
    try {
      const ctx = await captureContext({ includeScreenshot, light: true });
      const formData = new FormData();
      formData.append('payload', JSON.stringify({
        title: t.slice(0, MAX_TITLE),
        description: d.slice(0, MAX_DESCRIPTION),
        kind: 'feature',
        zone,
        nature,
        capture: ctx.capture,
        console_log: ctx.console_log,
        network_failures: ctx.network_failures,
        app_state: ctx.app_state,
      }));
      if (ctx.screenshot_blob) {
        formData.append('screenshot', ctx.screenshot_blob, 'screenshot.png');
      }

      const res = await fetch('/api/bug-reports', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        const errMsg = (data && (data.error || data.detail)) || `HTTP ${res.status}`;
        throw new Error(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
      }
      toast.success(`Idée #${data.bug_report?.number ?? '?'} envoyée. Merci !`);
      setTitle('');
      setDescription('');
      setZone('autre');
      setNature('new_feature');
      if (typeof onSubmitted === 'function') onSubmitted(data.bug_report);
      if (typeof onClose === 'function') onClose();
    } catch (err) {
      toast.error(`Échec de l'envoi : ${err.message || String(err)}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-bug-capture-skip="true"
      style={{
        background: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: 8,
        padding: 12,
        marginBottom: 16,
        fontFamily: "'IBM Plex Sans', sans-serif",
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        marginBottom: 10, fontSize: 12, fontWeight: 700, color: '#b45309',
      }}>
        <Lightbulb size={14} />
        <span>Proposer une idée</span>
      </div>

      <input
        type="text"
        placeholder="Titre court de l'idée (max 200 caractères)"
        value={title}
        onChange={e => setTitle(e.target.value)}
        maxLength={MAX_TITLE}
        disabled={submitting}
        style={inputStyle}
      />

      <textarea
        placeholder="Décris ton idée : que voudrais-tu pouvoir faire, et quel bénéfice ?"
        value={description}
        onChange={e => setDescription(e.target.value)}
        maxLength={MAX_DESCRIPTION}
        disabled={submitting}
        rows={5}
        style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
      />

      <select
        value={zone}
        onChange={e => setZone(e.target.value)}
        disabled={submitting}
        style={inputStyle}
        title="Zone du portail concernée"
      >
        {ZONES.map(z => (
          <option key={z.value} value={z.value}>{z.label}</option>
        ))}
      </select>

      <select
        value={nature}
        onChange={e => setNature(e.target.value)}
        disabled={submitting}
        style={inputStyle}
        title="Nature de l'idée"
      >
        {NATURES.map(n => (
          <option key={n.value} value={n.value}>{n.label}</option>
        ))}
      </select>

      <label style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 11, color: '#92400e', marginBottom: 10, cursor: 'pointer',
      }}>
        <input
          type="checkbox"
          checked={includeScreenshot}
          onChange={e => setIncludeScreenshot(e.target.checked)}
          disabled={submitting}
        />
        Joindre une capture d'écran de la page
      </label>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="submit"
          disabled={submitting}
          style={{
            flex: 1,
            background: submitting ? '#fcd34d' : '#d97706',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '8px 12px',
            fontSize: 12,
            fontWeight: 700,
            cursor: submitting ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          <Send size={12} />
          {submitting ? 'Envoi…' : 'Envoyer'}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          style={{
            background: 'transparent',
            color: '#92400e',
            border: '1px solid #fde68a',
            borderRadius: 6,
            padding: '8px 12px',
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          Annuler
        </button>
      </div>
    </form>
  );
}

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  marginBottom: 8,
  fontSize: 12,
  border: '1px solid #fde68a',
  borderRadius: 6,
  background: '#fff',
  color: 'var(--text)',
  fontFamily: "'IBM Plex Sans', sans-serif",
  boxSizing: 'border-box',
};
